import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { cpApi, setAuthToken } from '../api/api';

const LOGO_SRC = '/images/wt-logo.png';

function russifyLoginError(msg: string): string {
  if (msg.includes('access denied: use platform')) {
    return 'Этот аккаунт предназначен для платформы. Войдите через airex-platform.';
  }
  if (msg.includes('access denied: use counterparty cabinet')) {
    return 'Этот аккаунт предназначен для ЛК контрагента. Войдите через личный кабинет контрагента.';
  }
  return msg;
}

const SAVED_LOGIN_KEY = 'airex_cp_saved_login';
const REMEMBER_ME_KEY = 'airex_cp_remember_me';

export default function Login() {
  const [formData, setFormData] = useState({ login: '', password: '' });
  const [code2FA, setCode2FA] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem(SAVED_LOGIN_KEY);
    const remembered = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    if (saved && remembered) {
      setFormData((prev) => ({ ...prev, login: saved }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (show2FA && code2FA.trim()) {
        const res = await cpApi.loginWith2FA(formData.login, code2FA.trim());
        if (res.token && res.counterparty) {
          setAuthToken(res.token);
          if (rememberMe) {
            localStorage.setItem(SAVED_LOGIN_KEY, formData.login);
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
          } else {
            localStorage.removeItem(SAVED_LOGIN_KEY);
            localStorage.removeItem(REMEMBER_ME_KEY);
          }
          navigate('/requests', { replace: true });
        }
      } else {
        const res = await cpApi.login({ login: formData.login, password: formData.password });
        if (res.requires_2fa) {
          setShow2FA(true);
        } else if (res.requires_2fa_setup && res.token) {
          setAuthToken(res.token);
          if (rememberMe) {
            localStorage.setItem(SAVED_LOGIN_KEY, formData.login);
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
          } else {
            localStorage.removeItem(SAVED_LOGIN_KEY);
            localStorage.removeItem(REMEMBER_ME_KEY);
          }
          navigate('/settings', { replace: true });
        } else if (res.token && res.counterparty) {
          setAuthToken(res.token);
          if (rememberMe) {
            localStorage.setItem(SAVED_LOGIN_KEY, formData.login);
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
          } else {
            localStorage.removeItem(SAVED_LOGIN_KEY);
            localStorage.removeItem(REMEMBER_ME_KEY);
          }
          navigate('/requests', { replace: true });
        }
      }
    } catch (err: unknown) {
      const res = (err as { response?: { data?: string | { error?: string } } })?.response?.data;
      const msg = typeof res === 'string' ? res : (res?.error ?? 'Ошибка входа');
      setError(russifyLoginError(msg));
    } finally {
      setLoading(false);
    }
  };

  const handle2FABack = () => {
    setShow2FA(false);
    setCode2FA('');
    setError(null);
  };

  const errorBlock = error && (
    <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-3 rounded-xl backdrop-blur-sm">
      {error}
    </div>
  );

  const LogoBlock = ({ size = 'lg' }: { size?: 'sm' | 'lg' }) => {
    const h = size === 'lg' ? 'h-20 sm:h-24' : 'h-16 sm:h-20';
    const wh = size === 'lg' ? 'w-20 sm:w-24' : 'w-16 sm:w-20';
    return (
      <div className={`relative inline-flex items-center justify-center mx-auto mb-4 ${h} ${wh}`}>
        <div className="absolute inset-0 bg-white rounded-full scale-110" />
        <img src={LOGO_SRC} alt="WT" className="relative w-full h-full object-contain drop-shadow-lg" />
      </div>
    );
  };

  if (show2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-sky-900 flex items-start justify-center pt-16 sm:pt-20 pb-12 px-4 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.12] blur-3xl"
          style={{ backgroundImage: `url(${LOGO_SRC})`, backgroundSize: '80%' }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent" />

        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-6 sm:mb-8">
            <LogoBlock size="sm" />
            <div className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300 mb-2">
              WT КОНТРАГЕНТ
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-1">Двухфакторная аутентификация</h2>
            <p className="text-sm text-white/70">Введите код из приложения Google Authenticator</p>
          </div>
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/20">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="twofa_code" className="block text-sm font-medium text-white/90 mb-2">
                  Код 2FA
                </label>
                <input
                  id="twofa_code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={6}
                  value={code2FA}
                  onChange={(e) => setCode2FA(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-center text-xl tracking-[0.5em] font-mono"
                />
              </div>
              {errorBlock}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handle2FABack}
                  className="flex-1 py-3 px-4 border border-white/20 rounded-xl text-white bg-white/10 hover:bg-white/20 transition-all"
                >
                  Назад
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Проверка...
                    </span>
                  ) : (
                    'Войти'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-sky-900 flex items-start justify-center pt-16 sm:pt-20 pb-12 px-4 relative overflow-hidden">
      {/* Размытый логотип на фоне */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.12] blur-3xl"
        style={{ backgroundImage: `url(${LOGO_SRC})`, backgroundSize: '80%' }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent" />

      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-6 sm:mb-8">
          <LogoBlock size="lg" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Добро пожаловать!</h1>
          <div className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">
            WT КОНТРАГЕНТ
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/20">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="login" className="block text-sm font-medium text-white/90 mb-2">
                Логин
              </label>
              <input
                id="login"
                name="login"
                type="text"
                required
                placeholder="Введите ваш логин"
                value={formData.login}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
                Пароль
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                placeholder="Введите пароль"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-indigo-500 border-white/30 rounded bg-white/10"
              />
              <label htmlFor="remember-me" className="ml-2 text-sm text-white/90">
                Запомнить меня
              </label>
            </div>
            {errorBlock}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Вход...
                </span>
              ) : (
                'Войти'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
